import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Download, Search, Send, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { LoadingOverlay } from '@/components/shared'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { InvoiceModuleTabs } from '@/components/billing/InvoiceModuleTabs'
import {
  CreditNoteStatusBadge,
  ClientNameWithProspect,
} from '@/components/shared/StatusBadges'
import { DeleteCreditNoteDialog } from '@/components/billing/DeleteCreditNoteDialog'
import { SendCreditNoteDialog } from '@/components/billing/SendCreditNoteDialog'
import { Permit } from '@/components/guards/Permit'
import { useCreditNotesList } from '@/hooks/useInvoices'
import { useUrlListParams } from '@/hooks/useUrlListParams'
import { creditNotesApi } from '@/services/invoices'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { formatCurrency, formatDate } from '@/lib/format'
import { cn as classNames } from '@/lib/utils'
import {
  CREDIT_NOTE_REASON_LABELS,
  type CreditNote,
  type CreditNoteListQuery,
} from '@/types'

const thClass =
  'h-10 border-b border-border bg-muted px-3 py-2 text-xs font-medium normal-case tracking-normal text-muted-foreground'

const tdClass =
  'h-16 overflow-hidden border-b border-border px-3 py-2 align-middle text-sm'

const actionBtnClass =
  'inline-flex shrink-0 items-center justify-center rounded-full p-1 hover:bg-accent'

const actionIconClass = 'h-4 w-4 shrink-0'

/** Figma 1151:24655 column widths (8 columns, min table width 1152px). */
const COL = {
  number: 'w-[200px] min-w-[200px]',
  linkedInvoice: 'w-[200px] min-w-[200px]',
  clientName: 'w-[220px] min-w-[220px]',
  amount: 'w-[120px] min-w-[120px]',
  reason: 'w-[252px] min-w-[252px]',
  status: 'w-[100px] min-w-[100px]',
  createdDate: 'w-[120px] min-w-[120px]',
  actions: 'w-[80px] min-w-[80px]',
} as const

const TABLE_MIN_WIDTH = 1292
const COLUMN_COUNT = 8

// 2.6.9 Credit note list - searchable, paginated; per-row Send (2.6.11) /
// Export PDF (2.6.12).
export default function CreditNotesListPage() {
  const { t } = useTranslation('invoices')
  const navigate = useNavigate()
  const { page, setPage, limit, setLimit, search, onSearchChange, query } =
    useUrlListParams<Record<string, never>>({})

  const { data, isLoading, isError } = useCreditNotesList(
    query as CreditNoteListQuery
  )
  const creditNotes = data?.data ?? []
  const [sendTarget, setSendTarget] = useState<CreditNote | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CreditNote | null>(null)
  const [exportingPdfId, setExportingPdfId] = useState<string | null>(null)

  const exportPdf = async (cn: CreditNote) => {
    setExportingPdfId(cn.id)
    try {
      const res = await creditNotesApi.pdf(cn.id)
      if (!res.data?.url) throw new Error(t('shared:exportDialog.error'))
      window.open(res.data.url, '_blank', 'noopener')
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('creditNotes.pdfError')),
      })
    } finally {
      setExportingPdfId(null)
    }
  }

  return (
    <div className='flex flex-col gap-6'>
      <header className='flex shrink-0 flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <h1 className='text-2xl font-semibold leading-[1.2] tracking-[-0.48px]'>
            {t('creditNotes.title')}
          </h1>
          <p className='text-base leading-[1.4] text-muted-foreground'>
            {t('creditNotes.subtitle')}
          </p>
        </div>
      </header>

      {/* Invoices / Credit notes tabs (WBS 2.6.9 - credit notes live inside the
          Invoice management module). */}
      <div className='shrink-0'>
        <InvoiceModuleTabs active='credit-notes' />
      </div>

      {/* Toolbar (Figma 1196:32987): search right-aligned. */}
      <div className='flex shrink-0 justify-end'>
        <div className='relative w-full shrink-0 md:w-[320px]'>
          <Search className='pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B3B3B3]' />
          <Input
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={t('creditNotes.searchPlaceholder')}
            maxLength={100}
            className='h-10 w-full rounded-lg pl-10 pr-4 text-base font-normal placeholder:text-[#B3B3B3]'
          />
        </div>
      </div>

      <Card className='overflow-hidden shadow-sm'>
        <div className='overflow-x-auto'>
          <table
            className='w-full table-fixed caption-bottom text-sm'
            style={{ minWidth: TABLE_MIN_WIDTH }}
          >
            <TableHeader className='sticky top-0 z-10 bg-muted'>
              <TableRow className='border-0'>
                <TableHead className={classNames(thClass, COL.number)}>
                  {t('creditNotes.columns.number')}
                </TableHead>
                <TableHead className={classNames(thClass, COL.linkedInvoice)}>
                  {t('creditNotes.columns.linkedInvoice')}
                </TableHead>
                <TableHead className={classNames(thClass, COL.clientName)}>
                  {t('creditNotes.columns.clientName')}
                </TableHead>
                <TableHead className={classNames(thClass, COL.amount)}>
                  {t('creditNotes.columns.amount')}
                </TableHead>
                <TableHead className={classNames(thClass, COL.reason)}>
                  {t('creditNotes.columns.reason')}
                </TableHead>
                <TableHead className={classNames(thClass, COL.status)}>
                  {t('creditNotes.columns.status')}
                </TableHead>
                <TableHead className={classNames(thClass, COL.createdDate)}>
                  {t('creditNotes.columns.createdDate')}
                </TableHead>
                <TableHead className={classNames(thClass, COL.actions)}>
                  {t('creditNotes.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={classNames(
                      tdClass,
                      'py-10 text-center text-destructive'
                    )}
                  >
                    {t('creditNotes.loadError')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && creditNotes.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={classNames(
                      tdClass,
                      'py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('creditNotes.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                creditNotes.map(cn => (
                  <TableRow key={cn.id}>
                    <TableCell className={classNames(tdClass, COL.number)}>
                      <TruncatedTextTooltip
                        className='line-clamp-2 text-sm leading-[1.4] text-foreground'
                        content={cn.number ?? undefined}
                      >
                        {cn.number ?? '—'}
                      </TruncatedTextTooltip>
                    </TableCell>
                    <TableCell
                      className={classNames(tdClass, COL.linkedInvoice)}
                    >
                      {cn.invoice_id ? (
                        <button
                          type='button'
                          className='block min-w-0 max-w-full text-sm leading-[1.4] text-primary hover:underline'
                          onClick={() => navigate(`/invoices/${cn.invoice_id}`)}
                        >
                          <TruncatedTextTooltip
                            className='line-clamp-2'
                            content={
                              cn.invoice_number ?? t('creditNotes.viewInvoice')
                            }
                          >
                            {cn.invoice_number ?? t('creditNotes.viewInvoice')}
                          </TruncatedTextTooltip>
                        </button>
                      ) : (
                        <span className='text-sm text-muted-foreground'>—</span>
                      )}
                    </TableCell>
                    <TableCell className={classNames(tdClass, COL.clientName)}>
                      <div className='truncate text-sm leading-[1.4] text-foreground'>
                        <ClientNameWithProspect
                          name={cn.client?.name ?? cn.client_name ?? '—'}
                          category={cn.client?.category}
                        />
                      </div>
                    </TableCell>
                    <TableCell className={classNames(tdClass, COL.amount)}>
                      {formatCurrency(cn.total, 'EUR', 2, 'suffix')}
                    </TableCell>
                    <TableCell className={classNames(tdClass, COL.reason)}>
                      <TruncatedTextTooltip
                        className='line-clamp-2 text-sm leading-[1.4] text-foreground'
                        content={
                          cn.reason
                            ? (CREDIT_NOTE_REASON_LABELS[cn.reason] ??
                              cn.reason)
                            : undefined
                        }
                      >
                        {cn.reason
                          ? (CREDIT_NOTE_REASON_LABELS[cn.reason] ?? cn.reason)
                          : '—'}
                      </TruncatedTextTooltip>
                    </TableCell>
                    <TableCell className={classNames(tdClass, COL.status)}>
                      <CreditNoteStatusBadge status={cn.status} />
                    </TableCell>
                    <TableCell className={classNames(tdClass, COL.createdDate)}>
                      {formatDate(cn.created_at)}
                    </TableCell>
                    <TableCell className={classNames(tdClass, COL.actions)}>
                      <div className='flex items-center gap-2'>
                        {/* Credit notes are created from invoice detail; drafts remain deletable here. */}
                        {cn.status === 'draft' && (
                          <Permit module='invoices' action='delete'>
                            <button
                              type='button'
                              className={actionBtnClass}
                              title={t('creditNotes.delete')}
                              onClick={() => setDeleteTarget(cn)}
                            >
                              <Trash2
                                className={classNames(
                                  actionIconClass,
                                  'text-[#ec221f]'
                                )}
                              />
                            </button>
                          </Permit>
                        )}
                        <button
                          type='button'
                          className={actionBtnClass}
                          title={t('creditNotes.send')}
                          onClick={() => setSendTarget(cn)}
                        >
                          <Send
                            className={classNames(
                              actionIconClass,
                              'text-foreground'
                            )}
                          />
                        </button>
                        <button
                          type='button'
                          className={actionBtnClass}
                          title={t('creditNotes.export')}
                          onClick={() => exportPdf(cn)}
                        >
                          <Download
                            className={classNames(
                              actionIconClass,
                              'text-foreground'
                            )}
                          />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </table>
        </div>

        <ListPagination
          className='shrink-0 border-t border-border'
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
        />
      </Card>

      <SendCreditNoteDialog
        creditNote={sendTarget}
        onClose={() => setSendTarget(null)}
      />

      <DeleteCreditNoteDialog
        creditNote={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => setDeleteTarget(null)}
      />
      <LoadingOverlay open={exportingPdfId !== null} />
    </div>
  )
}
